'use client'

import React from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { TextH2, TextParagraph } from '@/components/ui/typography'
import { Join } from 'design-studio/lib'
import { staggerContainer, fadeInUp } from '@/components/ui/motion-variants'

// ── Utilities ────────────────────────────────────────────────────────────────

/** Split a string into individual word tokens. */
export function splitWords(text: string): string[] {
  return text.split(/\s+/).filter(Boolean)
}

function isInlineLinkEl(node: React.ReactElement): boolean {
  return node.type === 'a' || node.type === Link
}

/**
 * Flatten a ReactNode (e.g. title) into an ordered list of word strings and preserved elements (e.g. <br />).
 */
function flattenToTokens(
  node: React.ReactNode,
): Array<string | React.ReactElement> {
  if (typeof node === 'string') return splitWords(node)
  if (React.isValidElement(node)) {
    if (node.type === 'br') return [node]
    if (isInlineLinkEl(node)) return [node]
    const { children } = node.props as { children?: React.ReactNode }
    return children ? flattenToTokens(children) : []
  }
  if (Array.isArray(node)) return node.flatMap(flattenToTokens)
  return []
}

/** Plain text for sr-only / SEO when title tokens include links or other elements. */
function reactNodeToPlainText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) {
    return node.map(reactNodeToPlainText).filter(Boolean).join(' ')
  }
  if (React.isValidElement(node)) {
    if (node.type === 'br') return ' '
    const props = node.props as { children?: React.ReactNode }
    return reactNodeToPlainText(props.children)
  }
  return ''
}

// Stagger for title words: when section enters view, words fade in one by one (once).
const titleWordsContainer = {
  initial: {},
  animate: {
    transition: { staggerChildren: 0.12, delayChildren: 0 },
  },
}

const titleWord = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.6, ease: 'easeOut' as const },
  },
}

// Full text for screen readers (words + text inside links; <br /> → space).
function tokensToSrText(tokens: Array<string | React.ReactElement>): string {
  return tokens
    .map((t) => (typeof t === 'string' ? t : reactNodeToPlainText(t)))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// ── Staggered title words (in-view, once) ─────────────────────────────────────

export function TitleWords({ children }: { children: React.ReactNode }) {
  const tokens = flattenToTokens(children)
  const srText = tokensToSrText(tokens)
  return (
    <>
      <span className="sr-only">{srText}</span>
      <motion.span
        aria-hidden="true"
        variants={titleWordsContainer}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true, margin: '-200px 0px' }}
        style={{ display: 'inline' }}
      >
        {tokens.map((token, i) => {
          if (typeof token === 'string') {
            return (
              <motion.span
                key={`${token}-${i}`}
                variants={titleWord}
                className="mr-[0.28em] inline-block last:mr-0"
              >
                {token}
              </motion.span>
            )
          }
          if (React.isValidElement(token) && token.type === 'br') {
            return <React.Fragment key={`br-${i}`}>{token}</React.Fragment>
          }
          return (
            <motion.span
              key={`title-token-${i}`}
              variants={titleWord}
              className="mr-[0.28em] inline-block last:mr-0"
            >
              {token}
            </motion.span>
          )
        })}
      </motion.span>
    </>
  )
}

// ── SectionFooter ─────────────────────────────────────────────────────────────

interface SectionFooterProps {
  href: string
  children: React.ReactNode
  className?: string
}

export function SectionFooter({
  href,
  children,
  className,
}: SectionFooterProps) {
  return (
    <motion.div
      variants={fadeInUp}
      initial="initial"
      whileInView="animate"
      viewport={{ once: true }}
      className={Join('relative z-10 mt-12', className)}
    >
      <Link
        href={href}
        className="group inline-flex items-center gap-2 font-mono text-sm text-blue-500 transition-colors hover:text-blue-400 dark:text-blue-400 dark:hover:text-blue-300"
      >
        {children}
        <svg
          viewBox="0 0 12 12"
          className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-1"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path
            d="M1 6h10M7 2l4 4-4 4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Link>
    </motion.div>
  )
}

// ── SectionHeader ─────────────────────────────────────────────────────────────

interface SectionHeaderProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  /** Optional extra classes for the subtitle paragraph (merged with defaults via Join) */
  subtitleClassName?: string
  description?: React.ReactNode
  pretitle?: React.ReactNode
  pretitleColor?: string
  /** Full heading as single string for crawlers and screen readers */
  srOnlyFullHeading?: string
  className?: string
}

export function SectionHeader({
  title,
  subtitle,
  subtitleClassName,
  description,
  pretitle,
  pretitleColor = 'text-cyan-500/80',
  srOnlyFullHeading,
  className = 'relative z-10',
}: SectionHeaderProps) {
  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: '-200px 0px' }}
      className={Join(
        'mx-auto max-w-6xl',
        description
          ? 'grid grid-cols-1 items-start md:grid-cols-2 md:gap-3'
          : '',
        className,
      )}
    >
      {srOnlyFullHeading ? (
        <span className="sr-only">{srOnlyFullHeading}</span>
      ) : null}
      {pretitle && (
        <motion.div
          variants={fadeInUp}
          className={Join(
            'col-span-full mb-3 font-mono text-xs tracking-widest uppercase',
            pretitleColor,
          )}
        >
          {pretitle}
        </motion.div>
      )}
      <motion.div
        variants={fadeInUp}
        className={Join(subtitle ? 'relative flex flex-col gap-2' : '')}
      >
        <TextH2
          fontFamily="font-display"
          colors="text-blue-900 dark:text-white"
          text="text-4xl lg:text-5xl leading-relaxed"
          className="mix-blend-multiply [text-shadow:0_1px_2px_rgba(0,0,0,0.15)] dark:mix-blend-normal dark:[text-shadow:0_1px_3px_rgba(0,0,0,0.4)]"
        >
          <TitleWords>{title}</TitleWords>
        </TextH2>
        {subtitle && (
          <TextParagraph
            text="text-base lg:text-lg font-light leading-relaxed"
            colors="text-slate-600 dark:text-slate-300"
          >
            <span className={subtitleClassName}>{subtitle}</span>
          </TextParagraph>
        )}
      </motion.div>
      {description && (
        <motion.div variants={fadeInUp}>
          {typeof description === 'string' ? (
            <TextParagraph
              text="text-base lg:text-lg font-light leading-relaxed"
              colors="text-slate-600 dark:text-slate-300"
            >
              {description}
            </TextParagraph>
          ) : (
            <div>{description}</div>
          )}
        </motion.div>
      )}
    </motion.div>
  )
}
